/**
 * cart-addon-reconciler.js
 *
 * Paid add-ons ("DEAL" products) are added as plain, independent cart lines tagged with the
 * hidden property `_addon_parent_id` = their hero's product id (see subscription-selector.js →
 * addBundle). A deal only makes sense alongside its hero, so it must never outlive it: when the
 * hero product is no longer anywhere in the cart, the orphaned add-on line is auto-removed.
 *
 * Passive pass subscribed to `cartUpdate`: on each cart state, find add-on lines whose parent
 * hero product id is absent from the cart and remove them (quantity 0, keyed — never index-based)
 * in one /cart/update, then re-publish so the drawer/page re-renders. `seedCart()` runs the same
 * check against the cart already present on load, so a deal orphaned in a previous session is
 * cleaned up too.
 *
 * Only lines carrying `_addon_parent_id` are ever touched — a standalone purchase of the same
 * product (no such property) is left alone. Disjoint from cart-gift-reconciler.js (which owns
 * `_bundle_item` lines), so the two never conflict. Idempotent + re-entrancy guarded.
 */
(function () {
  if (window.__cartAddonReconcilerLoaded) return;
  window.__cartAddonReconcilerLoaded = true;

  let reconciling = false;
  let lastCart = null;

  const routes = () => window.FoxTheme.routes;
  const pubsub = () => window.FoxTheme.pubsub;

  // POST a key-keyed quantity update; returns the updated cart JSON.
  function postUpdate(updates) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return fetch(`${routes().cart_update_url}`, {
      ...window.FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
    }).then((response) => response.json());
  }

  function publishCart(cart, extra) {
    pubsub().publish(pubsub().PUB_SUB_EVENTS.cartUpdate, Object.assign({ cart }, extra || {}));
  }

  /**
   * Given cart items and an optional map of pending keyed quantity changes (line key → new qty),
   * return { addonLineKey: 0 } for every add-on whose parent hero product would NOT remain in the
   * cart after those changes. With an empty `pending` map it evaluates the cart as it stands.
   *
   * Exposed on window.CartAddonReconciler so cart-gift-reconciler.js can fold these removals into
   * the SAME atomic /cart/update as a hero (+ its gifts) removal — the deal then disappears in the
   * same request as the free gift, with no lagging second update.
   */
  function orphanAddonUpdates(items, pending) {
    pending = pending || {};
    const finalQty = (item) =>
      Object.prototype.hasOwnProperty.call(pending, item.key) ? pending[item.key] : item.quantity;
    const present = new Set();
    items.forEach((item) => {
      if (finalQty(item) > 0) present.add(String(item.product_id));
    });
    const updates = {};
    items.forEach((item) => {
      if (finalQty(item) <= 0) return; // this line is already leaving
      const parent = item.properties && item.properties._addon_parent_id;
      if (parent == null || parent === '') return;
      if (!present.has(String(parent))) updates[item.key] = 0;
    });
    return updates;
  }

  window.CartAddonReconciler = { orphanAddonUpdates };

  function reconcile(payload) {
    if (payload && payload.cart) lastCart = payload.cart;
    if (reconciling) return;

    const items = (lastCart && lastCart.items) || [];
    if (items.length === 0) return;

    const updates = orphanAddonUpdates(items);
    if (Object.keys(updates).length === 0) return;

    reconciling = true;
    postUpdate(updates)
      .then((cart) => {
        lastCart = cart;
        publishCart(cart);
      })
      .catch((error) => console.error('[addon-reconciler] reconcile failed', error))
      .finally(() => {
        reconciling = false;
      });
  }

  // Check the cart already present on load (catches a deal orphaned in a previous session).
  function seedCart() {
    fetch(`${routes().cart_url}`, { ...window.FoxTheme.utils.fetchConfig('json', 'GET') })
      .then((response) => response.json())
      .then((cart) => reconcile({ cart }))
      .catch(() => {});
  }

  function init() {
    if (!window.FoxTheme || !window.FoxTheme.pubsub) return false;
    pubsub().subscribe(pubsub().PUB_SUB_EVENTS.cartUpdate, reconcile);
    seedCart();
    return true;
  }

  if (!init()) {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
